import { Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { useClients } from 'src/clients/hooks/useClients';
import { useFamily } from 'src/clients/hooks/useFamily';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import PageLoader from 'src/UI/loader/PageLoader';
import { UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { useFileUploads } from 'src/data-capture/hooks/file-uploads/useFileUploads';
import { useFileTemporaryLinkMutation } from 'src/data-capture/hooks/useFileTemporaryLinkMutation';
import { dataUploadFileProperties, Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';
import { dispatchAppEvent } from 'src/common/events/appEvents';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { Logger } from 'src/utils/Logger';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import {
  ALLOWED_EXTENSIONS,
  FILE_BUTTON_ACCEPT,
} from 'src/data-capture/file-management/allowedFileTypes';
import { PickedItem } from 'src/data-capture/file-management/drop-zone/SharePointButton';
import { useSharePointPicker } from 'src/data-capture/file-management/drop-zone/SharePointProvider';
import FileUploadLayout from 'src/data-capture/file-management/FileUploadLayout';
import AddDocumentCard, {
  FiledInfo,
} from 'src/data-capture/file-management/components/AddDocumentCard';
import DocumentsDropZone from 'src/data-capture/file-management/components/DocumentsDropZone';
import DocumentsUploadLanding from 'src/data-capture/file-management/components/DocumentsUploadLanding';
import MoveFileModal from 'src/data-capture/file-management/files-table/MoveFileModal';
import { useFamilyFileActions } from './useFamilyFileActions';
import { useFamilyFileUploadActions } from './useFamilyFileUploadActions';
import { sameOwnerFolders } from './sameOwnerFolders';

const FILED_ROW_DURATION_MS = 3000;

export default function FamilyAddDocumentsPage() {
  const { familyId = '' } = useParams<{ familyId: string }>();
  const navigate = useNavigate();
  const { openPicker, pendingPickedItems, clearPendingPickedItems } = useSharePointPicker();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const filedTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const { family } = useFamily(familyId);
  const familyName = family?.name;
  const rootFolderLabel = familyName ? `All ${familyName} documents` : 'All family documents';
  const { clients } = useClients({ familyId }, { enabled: !!familyId });
  const clientNameById = Object.fromEntries(
    clients.map(c => [c.id, `${c.firstName} ${c.lastName}`]),
  );

  const [selectedFileForMove, setSelectedFileForMove] = useState<UploadedFile | null>(null);
  const [moveModalOpened, { open: openMoveModal, close: closeMoveModal }] = useDisclosure(false);
  const [filedInfo, setFiledInfo] = useState<Record<string, FiledInfo>>({});
  const [pendingFiles, setPendingFiles] = useState<UploadedFile[]>([]);

  const { files, filesToAdd, refetchFiles, isLoading, error } = useFileUploads({
    familyId,
    autoRefreshProcessing: true,
  });
  const {
    deleteFile,
    moveFile,
    updateDocumentDate,
    dismissDetectedClient,
    dismissingClientId,
    retryProcessing,
  } = useFamilyFileActions({ familyId });
  const { uploadFiles, uploadOneDriveFiles } = useFamilyFileUploadActions({ familyId });

  const [retryingFileId, setRetryingFileId] = useState<string | null>(null);

  const {
    createLink,
    isLoading: isCreatingLink,
    isError: creatingLinkFailed,
  } = useFileTemporaryLinkMutation();

  useEffect(() => {
    const timers = filedTimersRef.current;
    return () => {
      timers.forEach(timer => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  useEffect(() => {
    if (pendingPickedItems && pendingPickedItems.length > 0) {
      Logger.info('Processing pending OneDrive items from global provider', { pendingPickedItems });
      uploadOneDriveFilesDirectly(pendingPickedItems);
      clearPendingPickedItems();
    }
  }, [pendingPickedItems, clearPendingPickedItems]);

  const addPendingFiles = (fileNames: { name: string; size: number; type: string }[]) => {
    const newFiles: UploadedFile[] = fileNames.map(f => ({
      id: `pending-${uuidv4()}`,
      file: {
        id: 0,
        fileName: f.name,
        fileSize: f.size,
        fileType: f.type,
        isFolder: false,
        isResolved: false,
        isFiled: false,
        isTemporary: false,
        createdAt: new Date().toISOString(),
      },
      isFolder: false,
      isResolved: false,
      createdAt: new Date().toISOString(),
      detectedClients: [],
      backgroundProcess: {
        id: '',
        processName: '',
        status: 'PENDING' as const,
        statusMessage: '',
        extraDetails: null,
      },
    }));
    setPendingFiles(prev => [...prev, ...newFiles]);
    return newFiles;
  };

  const uploadWithOptimisticUI = <T,>(
    items: T[],
    toPendingInfo: (item: T) => { name: string; size: number; type: string },
    doUpload: () => Promise<UploadedFile[]>,
  ) => {
    const pending = addPendingFiles(items.map(toPendingInfo));
    const pendingIds = new Set(pending.map(f => f.id));
    doUpload()
      .then(uploadedFiles => {
        uploadedFiles.forEach(file => {
          Mixpanel.track(MixpanelEvents.DATA_UPLOAD_FILE_UPLOADED, dataUploadFileProperties(file));
        });
        dispatchAppEvent('DATA_UPLOADED');
      })
      .catch(error => {
        notifyAndLogError(
          'Failed to upload files',
          'An error occurred while uploading the files. Please try again later.',
          error,
          { familyId, fileCount: items.length },
        );
      })
      .finally(() => {
        refetchFiles()
          .catch(() => {})
          .finally(() => {
            setPendingFiles(prev => prev.filter(f => !pendingIds.has(f.id)));
          });
      });
  };

  const uploadFilesDirectly = (selected: File[]) => {
    uploadWithOptimisticUI(
      selected,
      f => ({ name: f.name, size: f.size, type: f.type }),
      () =>
        uploadFiles(selected.map(file => ({ file, modelId: undefined, documentDate: null }))).then(
          results => results.map(r => r.data),
        ),
    );
  };

  const uploadOneDriveFilesDirectly = (items: PickedItem[]) => {
    uploadWithOptimisticUI(
      items,
      i => ({ name: i.name || 'Unknown', size: i.size || 0, type: '' }),
      () => uploadOneDriveFiles({ items, documentDate: null, parentId: null }),
    );
  };

  const handleSharePointClick = (e?: React.MouseEvent<HTMLElement>) => {
    e?.preventDefault();
    e?.stopPropagation();
    openPicker(items => {
      Logger.info('picked items from OneDrive:', { items });
      uploadOneDriveFilesDirectly(items);
    }, ALLOWED_EXTENSIONS);
  };

  const handleSelectedInputFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []);
    if (selected.length > 0) uploadFilesDirectly(selected);
    event.target.value = '';
  };

  const handleDownloadFile = async (file: UploadedFile) => {
    try {
      const link = await createLink(file.file.id);
      window.open(link.url, '_blank');
    } catch (error) {
      notifyAndLogError(
        'Failed to open file',
        'An error occurred while trying to open the file. Please try again later.',
        error,
        { familyId, fileId: file.file.id },
      );
    }
  };

  const handleDeleteFile = async (file: UploadedFile) => {
    try {
      await deleteFile(file);
    } catch (error) {
      notifyAndLogError(
        'Failed to delete file',
        'An error occurred while trying to delete the file. Please try again later.',
        error,
        { familyId, clientId: file.clientId, fileId: file.id },
      );
    }
  };

  const handleUpdateDocumentDate = async (file: UploadedFile, date: Date) => {
    try {
      await updateDocumentDate(file, date);
    } catch (error) {
      notifyAndLogError(
        'Failed to update date',
        'An error occurred while trying to update the date. Please try again later.',
        error,
        { familyId, clientId: file.clientId, dataUploadFileId: file.id },
      );
    }
  };

  const handleDismissDetectedClient = async (file: UploadedFile, clientId: string) => {
    try {
      await dismissDetectedClient(file, clientId);
    } catch (error) {
      notifyAndLogError(
        'Failed to remove detected client',
        'An error occurred while trying to remove the detected client. Please try again later.',
        error,
        { familyId, fileId: file.id, clientId },
      );
    }
  };

  const handleRetryProcessing = async (file: UploadedFile) => {
    setRetryingFileId(file.id);
    try {
      await retryProcessing(file);
    } catch (error) {
      notifyAndLogError(
        'Failed to retry processing',
        'An error occurred while retrying. Please try again later.',
        error,
        { familyId, clientId: file.clientId, fileId: file.id },
      );
    } finally {
      setRetryingFileId(null);
    }
  };

  const handleProcessFile = (file: UploadedFile, detectedClientId?: string) => {
    Mixpanel.track(MixpanelEvents.DATA_UPLOAD_FILE_PROCESS_CLICK, dataUploadFileProperties(file));
    navigate(
      `/client/${detectedClientId ?? file.clientId}/detected-entities?fileId=${file.id}&fileName=${file.file.fileName}&guid=${uuidv4()}&familyId=${familyId}`,
    );
  };

  const handleFileTo = (file: UploadedFile) => {
    setSelectedFileForMove(file);
    openMoveModal();
  };

  const handleMoveFile = async (file: UploadedFile, parentId: string | null | undefined) => {
    try {
      await moveFile(file, parentId);

      const folderName = parentId
        ? files.find(f => f.id === parentId && f.isFolder)?.file.fileName || rootFolderLabel
        : rootFolderLabel;
      const existingCount = files.filter(
        f => f.id !== file.id && !f.isFolder && (f.parentId ?? null) === (parentId ?? null),
      ).length;

      setFiledInfo(prev => ({
        ...prev,
        [file.id]: {
          fileId: file.id,
          folderName,
          count: existingCount + 1,
          folderId: parentId ?? null,
        },
      }));

      const timer = setTimeout(() => {
        setFiledInfo(prev => {
          const next = { ...prev };
          delete next[file.id];
          return next;
        });
        filedTimersRef.current.delete(file.id);
        refetchFiles().catch(() => {});
      }, FILED_ROW_DURATION_MS);
      filedTimersRef.current.set(file.id, timer);
    } catch (error) {
      notifyAndLogError(
        'Failed to file document',
        'An error occurred while trying to file the document. Please try again later.',
        error,
        { familyId, clientId: file.clientId, fileId: file.id, parentId },
      );
    }
  };

  const handleOpenFiledFolder = (info: FiledInfo) => {
    const path = info.folderId
      ? `/family/${familyId}/documents/files/folder/${info.folderId}`
      : `/family/${familyId}/documents/files`;
    navigate(path, { state: { highlightFileId: info.fileId } });
  };

  if (error || creatingLinkFailed) {
    return <GenericErrorPage error={error || { creatingLinkFailed }} />;
  }

  if (isLoading || isCreatingLink) {
    return <PageLoader absolute={false} />;
  }

  const allFiles = [...filesToAdd, ...pendingFiles].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  return (
    <>
      <FileUploadLayout
        title="Add documents"
        familyId={familyId}
        familyName={familyName}
        hideHeader
      >
        {allFiles.length === 0 ? (
          <>
            <input
              type="file"
              multiple
              accept={FILE_BUTTON_ACCEPT}
              hidden
              ref={fileInputRef}
              onChange={handleSelectedInputFiles}
            />
            <DocumentsUploadLanding
              onUploadDocument={() => fileInputRef.current?.click()}
              onUploadFromSharePoint={handleSharePointClick}
            />
          </>
        ) : (
          <Stack gap="xxl">
            <DocumentsDropZone
              onFilesSelected={uploadFilesDirectly}
              onSharePointClick={handleSharePointClick}
            />
            <Stack gap="xxs">
              <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.4">
                To be processed
              </Text>
              <Stack gap="xxs" data-testid="recently-added-documents">
                {allFiles.map(file => (
                  <AddDocumentCard
                    key={file.id}
                    file={file}
                    filedInfo={filedInfo[file.id]}
                    rootFolderLabel={rootFolderLabel}
                    contactName={file.clientId ? (clientNameById[file.clientId] ?? null) : null}
                    onDownload={handleDownloadFile}
                    onDelete={handleDeleteFile}
                    onUpdateDate={handleUpdateDocumentDate}
                    onProcess={handleProcessFile}
                    onRetry={handleRetryProcessing}
                    isRetrying={retryingFileId === file.id}
                    onFileTo={handleFileTo}
                    onOpenFiledFolder={handleOpenFiledFolder}
                    onDismissDetectedClient={handleDismissDetectedClient}
                    dismissingClientId={dismissingClientId}
                  />
                ))}
              </Stack>
            </Stack>
          </Stack>
        )}
      </FileUploadLayout>

      {selectedFileForMove && (
        <MoveFileModal
          opened={moveModalOpened}
          close={closeMoveModal}
          file={selectedFileForMove}
          folders={sameOwnerFolders(files, selectedFileForMove)}
          rootLabel={rootFolderLabel}
          onFileMoved={handleMoveFile}
        />
      )}
    </>
  );
}
